import { useEffect, useState } from "react";
import { Maximize2 } from "lucide-react";
import { CELL_BOX } from "./InterviewCells";
import { LONG_ANSWER_MAX, longAnswerCount } from "@shared/long-answer";
import { saveInBackground, whenSaved } from "./saveFailure";

/**
 * s227 (BOX-FITS-1, D10) — the writing space's size, as the board draws it (Main.dc.html, the long-answer panel,
 * `row.bigs`): the panel `max-width: 760px` (rule 30's cap) and its box `min-height: 108px` (rule 30's floor).
 * `LocationBox.test.tsx` holds both, and the counter, equal to the board.
 */
export const WRITING_PANEL = { maxWidth: 760, minHeight: 108 } as const;

/**
 * s221 Lane 1, mechanism 4 — THE EXPANDING LOCATION BOX (rule 49, in-row form per D-220.15).
 *
 * "A location is one line that expands." In the row: a short box the planner can type straight into,
 * with an expand icon. Click the icon and a writing space opens UNDER THE ROW with a title, a hint,
 * a large box, Done and Cancel. Never an always-open textarea — Reade asked for that three times
 * before it held.
 *
 * Two pieces, because they render in two places: `LocationBox` sits in the cell, `LocationBoxPanel`
 * is passed to the row's `panels`. The screen holds `open` (rule 66 decides which one panel is open)
 * and the stored value; both pieces commit through the same `onCommit`.
 *
 * The same pair serves every long answer the SPEC draws with `long: true` and a `bigTitle` — a
 * child's health notes and their day, not only "where is it kept".
 *
 * Look (s221 Lane 1b, D-221.28): the board's 44px box with the expand arrow INSIDE its right corner.
 */

export function LocationBox({
  value,
  onCommit,
  placeholder,
  open,
  onOpen,
  testId = "interview-location",
}: {
  value: string;
  /** Called on blur of the short box with the value to store. A refused save leaves the draft in the box. */
  onCommit: (value: string) => void | Promise<unknown>;
  placeholder?: string;
  open: boolean;
  onOpen: () => void;
  testId?: string;
}) {
  const [draft, setDraft] = useState(value);
  useEffect(() => setDraft(value), [value]);

  return (
    <div className="relative">
      <input
        className={`${CELL_BOX} truncate pl-3 pr-9`}
        value={draft}
        placeholder={placeholder}
        aria-label={placeholder}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={(e) => { if (e.target.value !== value) saveInBackground(onCommit(e.target.value)); }}
        // s227 (BOX-FITS-1): the same answer as the panel, so the same real limit (the server refuses more).
        maxLength={LONG_ANSWER_MAX}
        data-testid={testId}
      />
      <button
        type="button"
        onClick={onOpen}
        aria-label="Open the writing space"
        aria-expanded={open}
        className="absolute right-1 top-1/2 flex h-9 w-8 -translate-y-1/2 items-center justify-center text-warm-muted hover:text-warm-charcoal"
        data-testid={`${testId}-expand`}
      >
        <Maximize2 className="h-3.5 w-3.5" aria-hidden="true" />
      </button>
    </div>
  );
}

export function LocationBoxPanel({
  value,
  onCommit,
  onClose,
  title,
  hint,
  placeholder,
  testId = "interview-location",
}: {
  value: string;
  /** s228 (INTERVIEW-SAVE-FAILURE, ruling 2): Done waits for this; a refused save keeps the panel open with the draft. */
  onCommit: (value: string) => void | Promise<unknown>;
  onClose: () => void;
  title: string;
  hint?: string;
  placeholder?: string;
  testId?: string;
}) {
  const [draft, setDraft] = useState(value);

  return (
    <div className="mb-3 rounded-[8px] border border-warm-stone bg-warm-white p-4" style={{ maxWidth: WRITING_PANEL.maxWidth }}
      data-testid={`${testId}-panel`}>
      <p className="flex flex-wrap items-baseline gap-x-3 gap-y-0.5">
        <span className="text-[15px] text-warm-charcoal">{title}</span>
        {hint && <span className="text-[13px] text-warm-muted">{hint}</span>}
      </p>
      <textarea
        rows={4}
        autoFocus
        value={draft}
        placeholder={placeholder}
        onChange={(e) => setDraft(e.target.value)}
        // s227 (BOX-FITS-1): the box stops at the limit; the server refuses more on every path (long-answer-limit.ts).
        maxLength={LONG_ANSWER_MAX}
        aria-describedby={`${testId}-count`}
        className="mt-2.5 w-full rounded-[8px] border border-warm-stone bg-warm-white px-3 py-2.5 text-[15px] text-warm-charcoal placeholder:text-warm-muted focus:outline-none focus:border-sage-600"
        style={{ minHeight: WRITING_PANEL.minHeight }}
        data-testid={`${testId}-panel-input`}
      />
      <div className="mt-2.5 flex items-center gap-4">
        <button
          type="button"
          onClick={() => { if (draft === value) onClose(); else whenSaved(onCommit(draft), onClose); }}
          className="h-11 rounded-[8px] bg-sage-600 px-5 text-[14px] text-warm-white hover:bg-sage-800"
          data-testid={`${testId}-done`}
        >
          Done
        </button>
        <button type="button" onClick={onClose} className="text-[14px] text-warm-muted hover:text-warm-charcoal" data-testid={`${testId}-cancel`}>
          Cancel
        </button>
        {/* s227 (BOX-FITS-1): the board's counter, "N / 600", on the right (Main.dc.html, `b.count`, 13px warm.muted, #4A4640 since s233). */}
        <span id={`${testId}-count`} className="ml-auto text-[13px] text-warm-muted" data-testid={`${testId}-count`}>
          {longAnswerCount(draft)}
        </span>
      </div>
    </div>
  );
}
